import { useEffect, useState } from "react";
import AuthApi from "../api/authApi";
import { emailFormatError, trimEmail } from "../utils/emailFormat";
import type { EmailDeliverabilityStatus } from "../utils/emailDeliverability";

const DEBOUNCE_MS = 500;
const authApi = new AuthApi();
const resultCache = new Map<string, Exclude<EmailDeliverabilityStatus, "idle" | "checking">>();
const domainCache = new Map<string, Exclude<EmailDeliverabilityStatus, "idle" | "checking">>();

function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at > 0 ? email.slice(at + 1).toLowerCase() : "";
}

function normalizeStatus(value: unknown): Exclude<EmailDeliverabilityStatus, "idle" | "checking"> {
  if (value === "valid" || value === "invalid" || value === "unknown") {
    return value;
  }
  return "unknown";
}

export function useEmailDeliverability(email: string): EmailDeliverabilityStatus {
  const [status, setStatus] = useState<EmailDeliverabilityStatus>("idle");

  useEffect(() => {
    const trimmed = trimEmail(email);
    if (!trimmed || emailFormatError(trimmed)) {
      setStatus("idle");
      return;
    }

    const key = trimmed.toLowerCase();
    const cached = resultCache.get(key) || domainCache.get(domainOf(key));
    if (cached) {
      setStatus(cached);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (cancelled) return;
      const fresh = resultCache.get(key) || domainCache.get(domainOf(key));
      if (fresh) {
        setStatus(fresh);
        return;
      }

      setStatus("checking");
      try {
        const res = await authApi.validateEmail({ email: trimmed });
        const next = normalizeStatus(res && res.status);
        resultCache.set(key, next);
        const domain = domainOf(key);
        if (domain) domainCache.set(domain, next);
        if (!cancelled) setStatus(next);
      } catch {
        if (!cancelled) setStatus("unknown");
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [email]);

  return status;
}
